import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import classes from './RegistrationPage.module.css';
import { getPacksApi, subApi, getSubStatusApi } from '../http/http';
import Cookies from 'js-cookie';
import { FaCheck } from 'react-icons/fa6';
import LanguageSelector from './LanguageSelector';
import { LanguageContext } from './LanguageContext';
import Loading from '../components/Loading';
import { REGISTER, COMMON, pageT } from '../utils/i18n';
import { countryCode, isValidUrl } from '../utils/constants';
import { toast } from 'react-toastify';

const PACKS = [
  { id: 1, price: '300 TSH', nameKey: 'dailyPass', descKey: 'dailyDesc' },
  { id: 2, price: '700 TSH', nameKey: 'weeklyPass', descKey: 'weeklyDesc' },
  { id: 3, price: '1500 TSH', nameKey: 'monthlyPass', descKey: 'monthlyDesc' },
];

const LANG_IDS = { eng: '1', sw: '2', ar: '3', fr: '4', es: '5' };

const RegistrationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { lang } = useContext(LanguageContext);
  const t = pageT(lang, REGISTER);
  const c = pageT(lang, COMMON);

  const [msisdn, setMsisdn] = useState(
    searchParams.get('mn') ? searchParams.get('mn') : ''
  );
  const [selectedPlan, setSelectedPlan] = useState(1);
  const [privacyAccepted, setPrivacyAccepted] = useState(true);
  const [flow, setFlow] = useState('');
  const [flowMsg, setFlowMsg] = useState({ number: '0', error: true });

  const [showChit, setShowChit] = useState(true);
  const [openChit, setOpenChit] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [closing, setClosing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);

  useEffect(() => {
    setTimeout(() => setOpenChit(true), 600);
    setTimeout(() => setShowForm(true), 1200);
  }, []);

  useEffect(() => {
    if (Cookies.get('token')) {
      navigate('/home');
      return;
    }

    setPageLoading(true);
    getPacksApi(searchParams.get('id') ? searchParams.get('id') : null)
      .then((res) => {
        setPageLoading(false);
        if (res?.token) {
          if (res.participant != null) {
            setFlow('EXISTING');
            if (res.participant === 1) {
              if (res?.tokenn) Cookies.set('token', res?.token);
              window.location.href = '/home';
            } else if (res.participant === 2) {
              setFlowMsg({ number: 'lowbalance', error: true });
            } else if (res.participant === 0) {
              setFlowMsg({ number: 'inprocess', error: true });
            } else {
              if (res.events.token) Cookies.set('token', res.events.token);
              window.location.href = '/home';
            }
          } else {
            if (res.events.token) Cookies.set('token', res.events.token);
            window.location.href = '/home';
          }
        }
      })
      .catch((err) => {
        setPageLoading(false);
        console.log(err);
      });
  }, []);

  const displayNumber =
    msisdn && msisdn.trim().length > 10 ? msisdn.trim().slice(3) : msisdn;

  const handleNumberChange = (e) => {
    const raw = e.target.value ? e.target.value.trim() : '';
    const digits = raw.replace(/\D/g, '');
    setMsisdn(digits.length <= 10 ? digits : digits.slice(0, 10));
  };

  const checkSubStatus = async (input) => {
    if (msisdn.length >= 8) {
      setLoading(true);
      try {
        const res = await getSubStatusApi({
          mobileNumber: parseInt(msisdn, 10),
        });
        setFlow('EXISTING');
        if (res.responseCode === 200) {
          if (res?.data?.accessToken)
            Cookies.set('token', res.data.accessToken);
          if (input === '1') {
            window.location.href = '/home';
          } else {
            setFlowMsg({ number: 'alreadySub', error: false });
          }
        } else if (res.responseCode === 203) {
          setFlowMsg({ number: 'lowbalance', error: true });
        } else if (res.responseCode === 202) {
          setFlowMsg({ number: 'inprocess', error: true });
        } else {
          setFlow('PACK');
        }
      } catch (err) {
        console.log(err);
        toast.error(t.errorMsg || c.serverError);
      } finally {
        setLoading(false);
      }
    } else {
      toast.error(t.invalidNumber);
    }
  };

  const handleSubscription = async (e) => {
    e.preventDefault();

    if (!msisdn?.trim().length) {
      toast.error(t.invalidNumber);
      return;
    }

    if (msisdn.length < 8) {
      toast.error(t.invalidNumber);
      return;
    }

    if (!privacyAccepted) {
      toast.error(t.acceptPrivacy);
      return;
    }

    setLoading(true);
    try {
      const res = await subApi({
        mobileNumber: msisdn,
        packId: selectedPlan,
        languageId: String(LANG_IDS[lang] ?? 2),
      });

      if (res?.responseCode === 202) {
        setFlow('EXISTING');
        setFlowMsg({ number: 'alreadySub', error: false });
      } else if (res?.data && isValidUrl(res.data)) {
        setFlow('REDIRECT');
        window.location.href = res.data;
      } else {
        setFlow('EXISTING');
        setFlowMsg({ number: 'subSuccess', error: false });
      }
    } catch (err) {
      console.log(err);
      toast.error(err.response?.data?.message || t.errorMsg || c.serverError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={classes.reg_container}>
      {(loading || pageLoading) && flow !== 'EXISTING' && <Loading />}

      <div className={classes.languageBox}>
        <LanguageSelector />
      </div>

      {!pageLoading && showChit && (
        <div
          className={`${classes.chit} 
            ${openChit ? classes.open : ''} 
            ${closing ? classes.closing : ''}`}
        >
          <div className={classes.flap}></div>

          <div className={classes.envelope_body}>
            {showForm && (
              <form
                className={`${classes.reg_box} ${classes.inside_chit} ${classes.slide_form}`}
                onSubmit={handleSubscription}
              >
                <h2 className={classes.reg_title}>{t.title}</h2>

                {flow === 'EXISTING' ? (
                  <>
                    <h3
                      className={`${classes.flowMsg} ${
                        flowMsg.error ? classes.flowMsgError : classes.flowMsgOk
                      }`}
                    >
                      {t[flowMsg.number]}
                    </h3>
                    <button
                      type="button"
                      disabled={loading}
                      onClick={() => checkSubStatus('1')}
                      className={classes.reg_btn}
                    >
                      {loading ? c.loading : t.clickHere}
                    </button>
                  </>
                ) : flow === 'REDIRECT' ? (
                  <h3 className={`${classes.flowMsg} ${classes.flowMsgOk}`}>
                    {t.redirect}
                  </h3>
                ) : (
                  <>
                    <label className={classes.phoneLabel}>
                      {t.numberLabel}
                    </label>
                    <div className={classes.phoneField}>
                      <div className={classes.phoneCode}>+{countryCode}</div>
                      <input
                        type="tel"
                        name="number"
                        placeholder={t.numberPlaceholder}
                        value={displayNumber}
                        onChange={handleNumberChange}
                        className={classes.phoneInput}
                      />
                    </div>

                    <div className={classes.plansWrap}>
                      {PACKS.map((pack) => {
                        const active = selectedPlan === pack.id;
                        return (
                          <button
                            key={pack.id}
                            type="button"
                            className={`${classes.planCard} ${
                              active ? classes.planCardActive : ''
                            }`}
                            onClick={() => setSelectedPlan(pack.id)}
                          >
                            <div className={classes.planInfo}>
                              <div
                                className={`${classes.planName} ${
                                  active ? classes.planNameActive : ''
                                }`}
                              >
                                {t[pack.nameKey]}
                              </div>
                              <div
                                className={`${classes.planDesc} ${
                                  active ? classes.planDescActive : ''
                                }`}
                              >
                                {t[pack.descKey]}
                              </div>
                              <div
                                className={`${classes.planPrice} ${
                                  active ? classes.planPriceActive : ''
                                }`}
                              >
                                {pack.price}
                              </div>
                            </div>
                            <div
                              className={`${classes.selector} ${
                                active ? classes.selectorActive : ''
                              }`}
                            >
                              {active && <FaCheck />}
                            </div>
                          </button>
                        );
                      })}
                    </div>

                    <div className={classes.privacyRow}>
                      <input
                        type="checkbox"
                        checked={privacyAccepted}
                        onChange={(e) => setPrivacyAccepted(e.target.checked)}
                      />
                      <Link
                        className={classes.privacyLabel}
                        to="/privacy-supplement"
                        state={{
                          msisdn,
                          pack: selectedPlan,
                        }}
                      >
                        {t.privacy}
                      </Link>
                    </div>

                    <button
                      className={classes.reg_btn}
                      type="submit"
                      disabled={loading}
                    >
                      {loading ? c.loading : t.subscribe}
                    </button>

                    <p className={classes.paymentNote}>{t.paymentNote}</p>
                    <Link className={classes.formLink} to={`/login`}>
                      {t.loginLink}
                    </Link>
                  </>
                )}
              </form>
            )}
          </div>
        </div>
      )}

      <div className={classes.floating_shapes}>
        <span></span>
        <span></span>
        <span></span>
        <span></span>
      </div>
    </div>
  );
};

export default RegistrationPage;
